import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LineController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { getBranchWiseDailyComparisonData } from "../../../../../../utils/Apis";
import { selectTabData } from "../../../../../../features/dynamicSlice";
import { Box, Chip } from "@mui/material";

// Register required Chart.js modules
ChartJS.register(
  LineElement,
  PointElement,
  LineController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

const BranchWiseChart = ({ branchIds, startDate, endDate }) => {
  const dispatch = useDispatch();
  const MemoizedLine = React.memo(Line);
  const chartData = useSelector((state) =>
    selectTabData(state, "branchWiseComparison")
  );
  const fetchedCache = useRef({});
  const [metricType, setMetricType] = useState("admissions"); // 'admissions' | 'revenue'

  useEffect(() => {
    if (!startDate || !endDate || !branchIds.length) return;

    const today = new Date().toISOString().split("T")[0];
    const safeStart = new Date(startDate) > new Date() ? today : startDate;
    const cacheKey = `${branchIds.join(",")}_${safeStart}_${endDate}`;

    if (!fetchedCache.current[cacheKey]) {
      dispatch(
        getBranchWiseDailyComparisonData({
          tab: "branchWiseComparison",
          filters: { branchIds, startDate: safeStart, endDate },
        })
      );
      fetchedCache.current[cacheKey] = true;
    }
  }, [branchIds, startDate, endDate, dispatch]);

  const getChartConfig = useMemo(() => {
    if (!chartData?.data?.length) return null;
    const branch = chartData.data[0];

    const currentMap = {};
    const previousMap = {};
    const allDays = new Set();

    branch.currentPeriod.forEach((item) => {
      const day = new Date(item.date).getDate().toString().padStart(2, "0");
      const value =
        metricType === "admissions"
          ? item.totalAdmissions
          : item.projectedRevenue || 0;
      currentMap[day] = (currentMap[day] || 0) + value;
      allDays.add(day);
    });

    branch.previousPeriod?.forEach((item) => {
      const day = new Date(item.date).getDate().toString().padStart(2, "0");
      const value =
        metricType === "admissions"
          ? item.totalAdmissions
          : item.projectedRevenue || 0;
      previousMap[day] = (previousMap[day] || 0) + value;
      allDays.add(day);
    });

    const labels = Array.from(allDays).sort((a, b) => Number(a) - Number(b));

    const currentData = labels.map((day) => currentMap[day] || 0);
    const previousData = labels.map((day) => previousMap[day] || 0);

    return {
      data: {
        labels,
        datasets: [
          {
            label:
              metricType === "admissions"
                ? "Current Period - Admissions"
                : "Current Period - Revenue",
            data: currentData,
            borderColor: "rgba(75,192,192,1)",
            fill: false,
            tension: 0.3,
          },
          ...(branch.previousPeriod?.length > 0
            ? [
                {
                  label:
                    metricType === "admissions"
                      ? "Previous Period - Admissions"
                      : "Previous Period - Revenue",
                  data: previousData,
                  borderColor: "rgba(255,99,132,1)",
                  fill: false,
                  tension: 0.3,
                },
              ]
            : []),
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              title: (tooltipItems) => {
                const label = tooltipItems[0].label;
                return `Day: ${label}`;
              },
              label: (context) => {
                const value = context.raw;
                return metricType === "admissions"
                  ? `Admissions: ${value}`
                  : `Revenue: ₹${value.toLocaleString()}`;
              },
            },
          },
        },
        interaction: {
          mode: "index",
          intersect: false,
        },
        scales: {
          x: {
            title: {
              display: true,
              text: "Day of Month",
            },
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: metricType === "admissions" ? "Admissions" : "Revenue (₹)",
            },
            ticks: {
              //   stepSize: 1,
              precision: 0,
            },
          },
        },
      },
    };
  }, [chartData, metricType]);

  if (!chartData?.data?.length) {
    return <p className="text-sm text-gray-500">No data to display</p>;
  }

  return (
    <Box>
      {/* Toggle Chips */}
      <Box sx={{ mb: 2, display: "flex", gap: 1 }}>
        <Chip
          label="Admissions"
          color={metricType === "admissions" ? "primary" : "default"}
          variant={metricType === "admissions" ? "filled" : "outlined"}
          onClick={() => setMetricType("admissions")}
        />
        <Chip
          label="Revenue"
          color={metricType === "revenue" ? "primary" : "default"}
          variant={metricType === "revenue" ? "filled" : "outlined"}
          onClick={() => setMetricType("revenue")}
        />
      </Box>

      {/* Line Chart */}
      <MemoizedLine
        data={getChartConfig.data}
        options={getChartConfig.options}
      />
    </Box>
  );
};

export default BranchWiseChart;
